import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChildOccupancyInput,
  CustomerInput,
  Exact,
  GuestOccupancyInput,
  InputMaybe,
  useCustomerNewMutation,
  useCustomerUpdateMutation,
  useReservedResourcesCheckInMutation,
  useUndoCheckinReservedResourcesMutation,
  useReservedResourcesCheckOutMutation,
  useUndoCheckoutReservedResourcesMutation,
  CheckinCheckoutReservedResourcesInput,
  ReservedResourcePhase,
} from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'
import syncMutation from 'src/utils/sync-mutation'
import { hasDuplicates } from 'src/utils/utils'
import { ReservationF } from '../types/reservation-detail.types'
import {
  RemoveGuestOccupancyOutput,
  UpdateCustomersAndGuestOccupanciesInput,
  UpdateCustomersAndGuestOccupanciesOutput,
  Customer,
  UpdateCustomerAndGuestOccupancyOutput,
} from './update-reserved-resource-guests.types'
import { fragmentToModel, inputToOutput, modelToInput } from './update-reserved-resource-guests.utils'
import { GuestCheckinCheckoutAction } from '../types/guests.types'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { guestReductionOperationType } from './reserved-resource-utils'

export function useUpdateReservedResourceGuests(reservation: ReservationF) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const tenantId = useTenant().id
  const hotelDate = useHotelDate()
  const { updateReservedResourceMutation } = useUpdateReservedResourceHook({ refetchQueries: ['ReservationDetail'] })

  const [createCustomerMutation] = useCustomerNewMutation()
  const [updateCustomerMutation] = useCustomerUpdateMutation()
  const [doCheckinMutation] = useReservedResourcesCheckInMutation()
  const [undoCheckinMutation] = useUndoCheckinReservedResourcesMutation()
  const [doCheckoutMutation] = useReservedResourcesCheckOutMutation()
  const [undoCheckoutMutation] = useUndoCheckoutReservedResourcesMutation()

  const updateCustomersAndGuestOccupancies = useCallback(
    //
    async (reservedResourceId: string, inputs: UpdateCustomersAndGuestOccupanciesInput): Promise<UpdateCustomersAndGuestOccupanciesOutput> => {
      const oldReservedResource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)
      if (!oldReservedResource) {
        return { error: new Error(`Cannot find reserved resource with id: ${reservedResourceId}`), customersAndGuestOccupancies: [] }
      }

      const oldGuestOccupancies = oldReservedResource.guestOccupancies || []

      const outputs: UpdateCustomerAndGuestOccupancyOutput[] = []
      const guestOccupancyInputs: GuestOccupancyInput[] = []

      for (let i = 0; i < inputs.length; i++) {
        const input = inputs[i]
        let customer: Customer | undefined = undefined
        let error: any = undefined
        switch (input.operation) {
          case 'CREATE': {
            const customerInput: CustomerInput = modelToInput(tenantId, input.guest)
            const customerResult = await syncMutation(createCustomerMutation, { variables: { input: customerInput } })
            const c = customerResult?.result?.data?.createCustomer
            customer = fragmentToModel(c)
            error = customerResult?.apolloError ?? customerResult.error
            break
          }
          case 'UPDATE': {
            const customerInput: CustomerInput = modelToInput(tenantId, input.guest)
            const variables: Exact<{
              id: string
              input: CustomerInput
              guestOfReservedResourceId?: InputMaybe<string> | undefined
            }> = {
              id: input.guest.id, //
              input: customerInput,
              guestOfReservedResourceId: reservedResourceId,
            }
            const customerResult = await syncMutation(updateCustomerMutation, { variables })
            const c = customerResult.result?.data?.updateCustomer
            customer = fragmentToModel(c)
            error = customerResult?.apolloError ?? customerResult.error
            break
          }
          case 'ASSIGN': {
            customer = { ...input.guest }
            error = undefined
            break
          }
          default:
            break
        }

        let output: UpdateCustomerAndGuestOccupancyOutput

        if (input.operation === 'CLEAR') {
          output = {
            occupancyIndex: null,
            output: inputToOutput(input, customer),
            error: error,
          }
        } else {
          let guestOccupancyInput: GuestOccupancyInput | null = null
          const oldGuestOccupancy = input.occupancyIndex !== null ? oldGuestOccupancies[input.occupancyIndex] : undefined
          if (customer) {
            guestOccupancyInput = {
              ...input.occupancy,
              guestId: customer.id,
            }
          } else if (oldGuestOccupancy) {
            guestOccupancyInput = {
              guestId: oldGuestOccupancy.guest.id,
              type: oldGuestOccupancy.type,
              start: oldGuestOccupancy.start,
              end: oldGuestOccupancy.end,
              cityTaxExoneration: oldGuestOccupancy.cityTaxExoneration,
            }
          }
          if (guestOccupancyInput) {
            guestOccupancyInputs.push(guestOccupancyInput)
          }
          output = {
            occupancyIndex: guestOccupancyInput ? guestOccupancyInputs.length - 1 : null,
            output: inputToOutput(input, customer),
            error: error,
          }
        }
        outputs.push(output)
      }

      const newGuestIds: string[] = guestOccupancyInputs.reduce((p: string[], v) => {
        return [...p, v.guestId]
      }, [])

      //FIXME: Il seguente controllo dovrà essere rimosso, per consentire l'inserimento di uno stesso ospite che ritorna sulla stessa reserved resource
      if (hasDuplicates(newGuestIds)) {
        return { error: new Error(`Duplicate guest`), customersAndGuestOccupancies: outputs }
      }

      const reservedResourceResult = await updateReservedResourceMutation({
        reservedResourceId: oldReservedResource.id,
        input: {
          tenantId: tenantId,
          adults: oldReservedResource.adults,
          chargeCategoryId: oldReservedResource.chargeCategory.id,
          children: oldReservedResource.children,
          rateId: oldReservedResource.rate.id,
          reservationId: oldReservedResource.reservationId,
          resourceOccupancies: oldReservedResource.resourceOccupancies?.map((ro) => {
            return {
              end: ro.end,
              start: ro.start,
              resourceTypeId: ro.reservedResourceType.id,
              resourceId: ro.resource?.id,
            }
          }),
          guestOccupancies: guestOccupancyInputs,
        },
      })
      const resultReservedResource = reservedResourceResult.result === 'SUCCESS' ? reservedResourceResult.reservedResource : undefined
      const resultError = reservedResourceResult.result === 'ERROR' ? reservedResourceResult.error : undefined

      for (let index = 0; index < outputs.length; index++) {
        const output = outputs[index]
        if (!resultReservedResource || resultError) {
          output.occupancyIndex = inputs[index].occupancyIndex
        } else if (output.occupancyIndex !== null) {
          output.output.phase = resultReservedResource?.guestOccupancies?.[output.occupancyIndex]?.phase ?? null
        }
      }

      return { reservedResource: resultReservedResource || oldReservedResource, customersAndGuestOccupancies: outputs, error: resultError }
    },
    [reservation, tenantId, hotelDate, updateReservedResourceMutation],
  )

  const reduceGuestCount = useCallback(
    //
    async (reservedResourceId: string, guestIndex: number, guestOccupancyIndex: number | null): Promise<RemoveGuestOccupancyOutput> => {
      const oldReservedResource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)
      if (!oldReservedResource) {
        return { error: new Error(`Cannot find reserved resource with id: ${reservedResourceId}`) }
      }
      const oldGuestOccupancies = oldReservedResource.guestOccupancies || []

      let children = oldReservedResource.children
      let adults = oldReservedResource.adults

      // const isChildGuest = oldGuestOccupancies.length === 0 || children >= 1

      // let operationType: 'CHILDREN_REDUCTION' | 'ADULTS_REDUCTION' | 'CHILD_CANCEL' | 'ADULT_CANCEL' | undefined = undefined
      // const splittedGo = splitGuestOccupancy(oldReservedResource)

      // if (isChildGuest) {
      //   if ((splittedGo.outGuests.children < 1 && splittedGo.notOutGuests.children === oldReservedResource.children) || splittedGo.notOutGuests.children === 0 || oldGuestOccupancies.length === 0) {
      //     operationType = 'CHILDREN_REDUCTION'
      //     children = oldReservedResource.children >= 1 ? oldReservedResource.children - 1 : oldReservedResource.children
      //   } else {
      //     operationType = 'CHILD_CANCEL'
      //   }
      // } else {
      //   if (splittedGo.outGuests.adults < 1 && splittedGo.notOutGuests.adults === oldReservedResource.adults) {
      //     operationType = 'ADULTS_REDUCTION'
      //     adults = oldReservedResource.adults > 1 ? oldReservedResource.adults - 1 : oldReservedResource.adults
      //   } else {
      //     operationType = 'ADULT_CANCEL'
      //   }
      // }

      switch (guestReductionOperationType(oldReservedResource)) {
        case 'CHILDREN_REDUCTION':
          children = oldReservedResource.children >= 1 ? oldReservedResource.children - 1 : oldReservedResource.children
          break
        case 'ADULTS_REDUCTION':
          adults = oldReservedResource.adults > 1 ? oldReservedResource.adults - 1 : oldReservedResource.adults
          break
      }

      if (adults !== null && adults <= 0 && adults !== oldReservedResource.adults) {
        return { error: new Error(t('pms.booking.reservation.guest.error.no_adults')) }
      }

      const newChildOccupancies: ChildOccupancyInput[] =
        oldReservedResource.childOccupancies?.reduce(
          //
          (p: ChildOccupancyInput[], v) => {
            const count = p.map((i) => i.quantity).reduce((pp, vv) => pp + vv, 0)
            const addable = children - count
            if (addable <= 0) return p

            return [
              ...p,
              {
                childId: v.child.id,
                quantity: Math.min(addable, v.quantity),
              },
            ]
          },
          [],
        ) ?? []

      const newGuestOccupancies: GuestOccupancyInput[] = oldGuestOccupancies.reduce((p: GuestOccupancyInput[], v, i) => {
        if (p.length >= adults + children) return p
        if (i === guestOccupancyIndex) return p
        return [
          ...p,
          {
            type: v.type,
            start: v.start,
            end: v.end,
            guestId: v.guest.id,
          },
        ]
      }, [])

      const reservedResourceResult = await updateReservedResourceMutation({
        reservedResourceId: oldReservedResource.id,
        input: {
          tenantId: tenantId,
          adults: adults,
          chargeCategoryId: oldReservedResource.chargeCategory.id,
          children: children,
          rateId: oldReservedResource.rate.id,
          reservationId: oldReservedResource.reservationId,
          resourceOccupancies: oldReservedResource.resourceOccupancies?.map((ro) => {
            return {
              end: ro.end,
              start: ro.start,
              resourceTypeId: ro.reservedResourceType.id,
              resourceId: ro.resource?.id,
            }
          }),
          guestOccupancies: newGuestOccupancies,
          childOccupancies: newChildOccupancies,
        },
      })

      const resultReservedResource = reservedResourceResult.result === 'SUCCESS' ? reservedResourceResult.reservedResource : undefined
      const resultError = reservedResourceResult.result === 'ERROR' ? reservedResourceResult.error : undefined
      return {
        reservedResource: resultReservedResource,
        error: resultError,
      }
    },
    [reservation, updateReservedResourceMutation],
  )

  const addGuest = useCallback(
    //
    async (reservedResourceId: string, childId: string | null): Promise<RemoveGuestOccupancyOutput> => {
      const oldReservedResource = reservation.reservedResources?.find((rr) => rr.id === reservedResourceId)
      if (!oldReservedResource) {
        return { error: new Error(`Cannot find reserved resource with id: ${reservedResourceId}`) }
      }

      if (childId !== null) {
        const child = oldReservedResource.rate.childRule?.childDiscount?.map((cd) => cd.child)?.find((c) => c.id === childId)
        if (!child) return { error: new Error(`Cannot find child with id: ${childId}`) }
      }

      const adults = childId === null ? oldReservedResource.adults + 1 : oldReservedResource.adults

      const newChildOccupancies: ChildOccupancyInput[] =
        oldReservedResource.childOccupancies?.map((co) => ({
          childId: co.child.id,
          quantity: co.quantity,
        })) ?? []

      if (childId !== null) {
        const childIndex = newChildOccupancies.findIndex((c) => c.childId === childId)
        if (childIndex !== -1) {
          newChildOccupancies[childIndex].quantity += 1
        } else {
          newChildOccupancies.push({ childId: childId, quantity: 1 })
        }
      }

      const children = newChildOccupancies.reduce((p, v) => p + v.quantity, 0)

      const reservedResourceResult = await updateReservedResourceMutation({
        reservedResourceId: oldReservedResource.id,
        input: {
          tenantId: tenantId,
          adults: adults,
          chargeCategoryId: oldReservedResource.chargeCategory.id,
          children: children,
          rateId: oldReservedResource.rate.id,
          reservationId: oldReservedResource.reservationId,
          resourceOccupancies: oldReservedResource.resourceOccupancies?.map((ro) => {
            return {
              end: ro.end,
              start: ro.start,
              resourceTypeId: ro.reservedResourceType.id,
              resourceId: ro.resource?.id,
            }
          }),
          childOccupancies: newChildOccupancies,
        },
      })

      const resultReservedResource = reservedResourceResult.result === 'SUCCESS' ? reservedResourceResult.reservedResource : undefined
      const resultError = reservedResourceResult.result === 'ERROR' ? reservedResourceResult.error : undefined
      return {
        reservedResource: resultReservedResource,
        error: resultError,
      }
    },
    [reservation],
  )

  const performGuestCheckinCheckout = useCallback(
    async (reservedResourceId: string, guestId: string, action: GuestCheckinCheckoutAction, onSuccess: (newPhase: ReservedResourcePhase) => void) => {
      const variables: { input: CheckinCheckoutReservedResourcesInput } = {
        input: {
          tenantId,
          reservedResources: [{ reservedResourceId: reservedResourceId, guestIds: [guestId] }],
        },
      }

      try {
        let result = undefined
        let resultPhase: ReservedResourcePhase | undefined = undefined
        switch (action) {
          case 'DO_CHECKIN':
            result = await doCheckinMutation({ variables })
            resultPhase = ReservedResourcePhase.CHECKIN
            break
          case 'UNDO_CHECKIN':
            result = await undoCheckinMutation({ variables })
            resultPhase = ReservedResourcePhase.RESERVATION
            break
          case 'DO_CHECKOUT':
            result = await doCheckoutMutation({ variables })
            resultPhase = ReservedResourcePhase.CHECKOUT
            break
          case 'UNDO_CHECKOUT':
            result = await undoCheckoutMutation({ variables })
            resultPhase = ReservedResourcePhase.CHECKIN
            break
        }
        if (result && resultPhase && !result.errors) {
          showSuccess(t('pms.booking.reservation.reserved_resource_edit.phase.success'))
          onSuccess(resultPhase)
        }
        if (result.errors) {
          showError(result.errors)
        }
      } catch (errors) {
        showError(errors)
      }
    },
    [doCheckinMutation, undoCheckinMutation, doCheckoutMutation, undoCheckoutMutation],
  )

  return { updateCustomersAndGuestOccupancies, reduceGuestCount, addGuest, performGuestCheckinCheckout }
}
